import { Button, Result, Spin } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router';

import type { UserRole } from '@/api/generated/model';
import { hasRole } from '@/auth/roles';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { problemMessage } from '@/i18n/problem';
import { isDesktop, serverUrl } from '@/runtime';

/** Всё приложение, кроме /login: без входа — на страницу входа с возвратом обратно. */
export function RequireAuth() {
  const { t } = useTranslation();
  const { user, isLoading, error } = useCurrentUser();
  const location = useLocation();
  if (isDesktop && !serverUrl()) return <Navigate to="/server" replace />;
  if (isLoading) return <FullPageSpin />;
  if (error) {
    return (
      <Result
        status="warning"
        title={t('pages.serverDown')}
        subTitle={problemMessage(error)}
        extra={[
          <Button
            key="retry"
            onClick={() => {
              window.location.reload();
            }}
          >
            {t('common.retry')}
          </Button>,
          ...(isDesktop
            ? [
                <Button key="server" href="/server">
                  {t('server.change')}
                </Button>,
              ]
            : []),
        ]}
      />
    );
  }
  if (!user) {
    const next = location.pathname + location.search;
    const to = next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`;
    return <Navigate to={to} replace />;
  }
  return <Outlet />;
}

/** Раздел для роли не ниже `min`. Кнопки и меню прячутся отдельно; это — защита по URL. */
export function RequireRole({ min, children }: { min: UserRole; children: ReactNode }) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  if (!user || !hasRole(user.role, min)) {
    return <Result status="403" title={t('pages.forbidden')} subTitle={t('pages.forbiddenHint')} />;
  }
  return children;
}

export function FullPageSpin() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <Spin size="large" />
    </div>
  );
}
